import { z } from 'zod';
import { parseLibrary } from '@orbit-mind/domain';
import { MAX_BACKUP_BYTES, createMapBackup, readMapBackup, prepareBackupCopy, } from './backup';
const encoder = new TextEncoder();
const schema = z
    .object({
    format: z.literal('orbit-mind-library'),
    version: z.literal(1),
    createdAt: z.number().int().nonnegative(),
    payload: z.string().max(MAX_BACKUP_BYTES),
    checksum: z.string().regex(/^[a-f0-9]{64}$/),
})
    .strict();
function organizationOf(value, maps) {
    const valid = z
        .object({
        folders: z
            .array(z
            .object({
            id: z.string().min(1).max(200),
            name: z.string().max(100),
            parentId: z.string().min(1).max(200).nullable(),
            color: z.string().regex(/^#[a-f0-9]{6}$/i),
            trashed: z.boolean(),
        })
            .strict())
            .max(1000),
        placements: z.record(z.string(), z
            .object({
            folderId: z.string().nullable(),
            tags: z.array(z.string().max(40)).max(20),
            favorite: z.literal(true).optional(),
        })
            .strict()),
    })
        .strict()
        .parse(value);
    const normalized = parseLibrary(valid);
    if (normalized.folders.length !== valid.folders.length)
        throw Error('Organização da biblioteca inválida.');
    const ids = new Set(maps.map((map) => map.map.id));
    const folders = new Set(normalized.folders.map((folder) => folder.id));
    for (const folder of normalized.folders) {
        const seen = new Set();
        let next = folder.id;
        while (next !== null) {
            if (seen.has(next) || !folders.has(next))
                throw new Error('Referências de pastas inválidas.');
            seen.add(next);
            next = normalized.folders.find((item) => item.id === next)?.parentId ?? null;
        }
    }
    for (const [id, placement] of Object.entries(valid.placements)) {
        if (!ids.has(id) || (placement.folderId !== null && !folders.has(placement.folderId)))
            throw Error('Referências de mapas inválidas na biblioteca.');
    }
    return normalized;
}
/** Includes the complete local library, even trash. Derived presentations travel with their maps. */
export async function createLibraryBackup(input) {
    if (input.documents.length > 1000 ||
        new Set(input.documents.map((doc) => doc.map.id)).size !== input.documents.length)
        throw new Error('Biblioteca inválida ou acima de 1.000 mapas por backup.');
    const organization = organizationOf(input.organization, input.documents);
    const maps = [];
    let size = 0;
    for (const document of input.documents) {
        const text = await createMapBackup({ ...input, document });
        size += encoder.encode(text).length;
        if (size > MAX_BACKUP_BYTES)
            throw new Error('Backup da biblioteca acima de 100 MiB. Faça backups individuais.');
        maps.push(text);
    }
    const payload = JSON.stringify({ maps, organization });
    const text = JSON.stringify({
        format: 'orbit-mind-library',
        version: 1,
        createdAt: input.now,
        payload,
        checksum: await input.hash(encoder.encode(payload)),
    });
    if (encoder.encode(text).length > MAX_BACKUP_BYTES)
        throw new Error('Backup da biblioteca acima de 100 MiB. Faça backups individuais.');
    return text;
}
export async function readLibraryBackup(text, hash) {
    if (text.length > MAX_BACKUP_BYTES || encoder.encode(text).length > MAX_BACKUP_BYTES)
        throw new Error('Limite do backup: 100 MiB.');
    const envelope = schema.parse(JSON.parse(text));
    if ((await hash(encoder.encode(envelope.payload))) !== envelope.checksum)
        throw new Error('A biblioteca está corrompida.');
    const payload = z
        .object({
        maps: z.array(z.string().max(MAX_BACKUP_BYTES)).max(1000),
        organization: z.unknown(),
    })
        .strict()
        .parse(JSON.parse(envelope.payload));
    const maps = [];
    for (const raw of payload.maps)
        maps.push(await readMapBackup(raw, hash));
    if (new Set(maps.map((map) => map.document.map.id)).size !== maps.length)
        throw new Error('Mapas duplicados no backup.');
    return {
        maps,
        organization: organizationOf(payload.organization, maps.map((map) => map.document)),
        createdAt: envelope.createdAt,
    };
}
function mapped(values, key) {
    const value = values.get(key);
    if (value === undefined)
        throw Error('Referência ausente ao restaurar.');
    return value;
}
/** Restores into a new folder tree; links between restored maps target the new copies. */
export function prepareLibraryCopy(backup, target) {
    const ids = new Map(backup.maps.map((map) => [map.document.map.id, target.newMapId()]));
    if (new Set(ids.values()).size !== ids.size)
        throw new Error('Identidades repetidas ao restaurar.');
    const folders = new Map(backup.organization.folders.map((folder, index) => [
        folder.id,
        `${target.prefix}_f${String(index)}`,
    ]));
    const maps = backup.maps.map((backupMap) => {
        const copy = prepareBackupCopy(backupMap, {
            ...target,
            mapId: mapped(ids, backupMap.document.map.id),
        });
        const nodes = Object.fromEntries(Object.entries(copy.document.nodes).map(([id, node]) => {
            const link = node.metadata?.mapLink;
            return [
                id,
                typeof link === 'object' &&
                    link !== null &&
                    'mapId' in link &&
                    typeof link.mapId === 'string' &&
                    ids.has(link.mapId)
                    ? {
                        ...node,
                        metadata: {
                            ...node.metadata,
                            mapLink: { ...link, mapId: ids.get(link.mapId) },
                        },
                    }
                    : node,
            ];
        }));
        return {
            ...copy,
            document: {
                ...copy.document,
                nodes,
                map: { ...copy.document.map, deletedAt: backupMap.document.map.deletedAt },
            },
        };
    });
    return {
        maps,
        organization: {
            folders: backup.organization.folders.map((folder) => ({
                ...folder,
                id: mapped(folders, folder.id),
                parentId: folder.parentId === null ? null : mapped(folders, folder.parentId),
            })),
            placements: Object.fromEntries(Object.entries(backup.organization.placements).map(([id, placement]) => [
                mapped(ids, id),
                {
                    ...placement,
                    folderId: placement.folderId === null ? null : mapped(folders, placement.folderId),
                },
            ])),
        },
    };
}
export async function readPortableBackup(text, hash) {
    if (text.length > MAX_BACKUP_BYTES)
        throw new Error('Limite do backup: 100 MiB.');
    const envelope = JSON.parse(text);
    return typeof envelope === 'object' &&
        envelope !== null &&
        'format' in envelope &&
        envelope.format === 'orbit-mind-library'
        ? { kind: 'library', value: await readLibraryBackup(text, hash) }
        : { kind: 'map', value: await readMapBackup(text, hash) };
}
//# sourceMappingURL=library-backup.js.map